import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '../../ui/radix/Dialog';
import { Button, Field, Input, Callout } from '../../ui';
import { Icon } from '../../ui/Icon';
import { Kicker } from '../../ui/Kicker';
import { useAuth } from '../../../context/AuthContext';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const AuthDialog: React.FC<AuthDialogProps> = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login({ email: data.email, password: data.password });
      onSuccess();
      reset();
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to login. Please try again.');
      }
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const handleSignupRedirect = () => {
    handleClose();
    navigate('/signup');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      maxWidth={440}
    >
      <div style={{ marginBottom: 22 }}>
        <Kicker>
          <Icon name="lock" size={11} /> Sign in
        </Kicker>
        <h2
          style={{
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            marginTop: 10,
          }}
        >
          Welcome back
        </h2>
        <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
          Sign in to view shipping rates and complete your booking.
        </p>
      </div>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <Icon name="alertCircle" size={16} />
            <span>{error}</span>
          </Callout>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="you@example.com"
            {...register('email')}
            style={errors.email ? { borderColor: 'var(--err)' } : undefined}
          />
          {errors.email && (
            <span style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>
              {errors.email.message}
            </span>
          )}
        </Field>

        <div style={{ marginTop: 14 }}>
          <Field label="Password">
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                style={{
                  paddingInlineEnd: 40,
                  ...(errors.password ? { borderColor: 'var(--err)' } : {}),
                }}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  insetInlineEnd: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--r-sm)',
                }}
              >
                <Icon name="eye" size={15} />
              </button>
            </div>
            {errors.password && (
              <span style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>
                {errors.password.message}
              </span>
            )}
          </Field>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 8,
            marginBottom: 18,
          }}
        >
          <Link
            to="/reset-password"
            onClick={handleClose}
            style={{ fontSize: 12, color: 'var(--brand-blue)', textDecoration: 'none' }}
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" variant="primary" block size="lg" disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
          <Icon name="arrowRight" size={15} />
        </Button>
      </form>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '20px 0 16px',
        }}
      >
        <div className="divider" style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
          OR
        </span>
        <div className="divider" style={{ flex: 1 }} />
      </div>

      <Button variant="secondary" block onClick={handleSignupRedirect}>
        <Icon name="plus" size={14} />
        Create new account
      </Button>

      <p
        className="muted"
        style={{
          fontSize: 11,
          textAlign: 'center',
          marginTop: 18,
          lineHeight: 1.55,
        }}
      >
        By continuing, you agree to Almaship's{' '}
        <Link
          to="/terms"
          onClick={handleClose}
          style={{ color: 'var(--brand-blue)', textDecoration: 'none' }}
        >
          Terms
        </Link>{' '}
        and{' '}
        <Link
          to="/privacy"
          onClick={handleClose}
          style={{ color: 'var(--brand-blue)', textDecoration: 'none' }}
        >
          Privacy Policy
        </Link>
        .
      </p>
    </Dialog>
  );
};

export default AuthDialog;
